/**
 * Contract Create page — orchestrator.
 * All business logic lives in modules/contract/*.ts and lib/*.ts.
 */

import { setupOfferGroups, populateSelects } from '../modules/contract/offer-groups';
import { setupAgencyHandler } from '../modules/contract/agency-handler';
import { setupTravellerManager } from '../modules/contract/traveller-manager';
import { setupTravellerRow } from '../modules/contract/traveller-row';
import { setupTransportToggle, syncNewTravellerCard } from '../modules/contract/transport-toggle';
import { syncContractDatesFromAccommodation } from '../modules/contract/contract-dates';
import { autoFillAllTravellers, getAllSelectedOfferIds } from '../modules/contract/auto-fill';
import { updatePriceSummary, setupPriceSummaryListeners } from '../modules/contract/price-calculator';
import { setupTravellerSync } from '../modules/contract/field-sync';
import { setupTravellerAutocomplete, fillTravellerFields } from '../lib/traveller-autocomplete';
import * as offerCache from '../modules/contract/offer-cache';
import type { PriceSummaryElements } from '../modules/contract/price-calculator';
import { initAiPropose } from '../modules/contract/contract-ai-propose';
import { initContractAssistant } from '../modules/contract/contract-assistant';
import { setupWizard } from '../modules/contract/wizard';
import { setupOfferListboxes } from '../modules/contract/offer-listbox';
import { getAgencyId, getAgencyName, getOfferGroupElements } from '../modules/contract/contract-dom';
import { createCalendarPrefill } from '../modules/contract/calendar-prefill';

document.addEventListener('DOMContentLoaded', () => {
    const form = document.getElementById('create-contract-form') as HTMLFormElement | null;
    if (!form) return;

    setupWizard(form);

    // ── Group selects (bail out if any are missing) ──
    const accommodationSelect = document.getElementById('AccommodationOfferIds') as HTMLSelectElement | null;
    const transportSelect     = document.getElementById('TransportOfferIds')     as HTMLSelectElement | null;
    const insuranceSelect     = document.getElementById('InsuranceOfferIds')     as HTMLSelectElement | null;
    if (!accommodationSelect || !transportSelect || !insuranceSelect) return;

    // Replace native <select> elements with styled custom listboxes
    setupOfferListboxes(accommodationSelect, transportSelect, insuranceSelect);

    // ── API URLs from data attributes ──
    const urls = {
        getOfferDetails:           form.dataset.urlOfferDetails           || '',
        getOfficesForAgency:       form.dataset.urlOfficesForAgency       || '',
        getOffersForAgency:        form.dataset.urlOffersForAgency        || '',
        getPartnerOffersForAgency: form.dataset.urlPartnerOffersForAgency || '',
        getVatForAgency:           form.dataset.urlVatForAgency           || '',
        searchTravellers:          form.dataset.urlSearchTravellers       || '',
    };

    // ── Shared DOM element bag (built once, passed to both setupOfferGroups and populateSelects) ──
    const offerGroupEls = getOfferGroupElements(accommodationSelect, transportSelect, insuranceSelect);
    const { accommodationPricesContent } = offerGroupEls;

    // ── Price summary ──
    const summaryCard = document.getElementById('priceSummaryCard') as HTMLElement;
    const summaryElements: PriceSummaryElements = {
        summaryCard,
        subtotalEl:      document.getElementById('summarySubtotal')!,
        discountLabelEl: document.getElementById('summaryDiscountLabel')!,
        discountEl:      document.getElementById('summaryDiscount')!,
        vatLabelEl:      document.getElementById('summaryVatLabel')!,
        vatEl:           document.getElementById('summaryVat')!,
        totalEl:         document.getElementById('summaryTotal')!,
        discountInput:   document.getElementById('Discount') as HTMLInputElement | null,
        vatPct:          parseFloat(summaryCard?.dataset.vatPct || '0') || 0,
    };

    const recalcPrices = () => updatePriceSummary(summaryElements);

    const onPriceChange = () => {
        const ids = getAllSelectedOfferIds(accommodationSelect, transportSelect, insuranceSelect);
        autoFillAllTravellers(ids, offerCache.getAll(), document.getElementById('passengersContainer')!, () => getAgencyName());
        syncContractDatesFromAccommodation(
            accommodationPricesContent,
            document.getElementById('CheckInDate')  as HTMLInputElement | null,
            document.getElementById('CheckOutDate') as HTMLInputElement | null,
        );
        prefill.applyDates();
    };

    // ── Offer groups ──
    setupOfferGroups({ ...offerGroupEls, summaryElements, urls, getAgencyId: () => getAgencyId(form), onPriceChange });

    // ── Calendar prefill (from /AvailabilityCalendar drag-to-create) ──
    const prefillDiv = document.getElementById('calendar-prefill');
    const prefill = createCalendarPrefill({
        prefillDiv,
        prefillOfferId:     prefillDiv ? parseInt(prefillDiv.dataset.prefillOfferId     ?? '') : NaN,
        prefillOfferUnitId: prefillDiv ? parseInt(prefillDiv.dataset.prefillOfferUnitId ?? '') : NaN,
        prefillCheckIn:     prefillDiv?.dataset.prefillCheckIn  ?? '',
        prefillCheckOut:    prefillDiv?.dataset.prefillCheckOut ?? '',
        accommodationSelect,
        accommodationPricesContent,
    });

    // ── Agency change ──
    setupAgencyHandler({
        agencySelect: document.getElementById('AgencyId') as HTMLSelectElement | null,
        officeSelect:  document.getElementById('OfficeId') as HTMLSelectElement,
        urls,
        onVatResolved: (vatPct) => {
            summaryElements.vatPct = vatPct;
            summaryCard.dataset.vatPct = String(vatPct);
            updatePriceSummary(summaryElements);
        },
        onLoaded: (accommodation, transport, insurance) => {
            populateSelects(accommodation, transport, insurance, { ...offerGroupEls, summaryElements, onPriceChange });
            prefill.applyOffer();
        },
        onReset: () => { onPriceChange(); },
    });

    // ── Price summary listeners ──
    setupPriceSummaryListeners(summaryElements, recalcPrices);

    // ── Transport toggle (show/hide "Use transport" per card) ──
    setupTransportToggle(recalcPrices);

    // ── Owner row: field sync + autocomplete + transport sync ──
    setupTravellerSync('ContractOwner', 'ContractOwner.ContactDetails');

    const ownerCard = document.querySelector<HTMLElement>('.traveller-card[data-is-owner="true"]');
    if (ownerCard) {
        setupTravellerRow(ownerCard);
        syncNewTravellerCard(ownerCard, recalcPrices);
        ownerCard.querySelector<HTMLInputElement>('.traveller-uses-transport')
            ?.addEventListener('change', recalcPrices);
    }

    const ownerSearchInput   = document.getElementById('ownerTravellerSearch')        as HTMLInputElement | null;
    const ownerSearchResults = document.getElementById('ownerTravellerSearchResults') as HTMLElement    | null;
    if (ownerSearchInput && ownerSearchResults) {
        setupTravellerAutocomplete(ownerSearchInput, ownerSearchResults, {
            searchUrl:      urls.searchTravellers,
            agencyIdGetter: () => getAgencyId(form),
            onSelect: (t) => fillTravellerFields(t, 'ContractOwner', 'ContractOwner.ContactDetails', 'ownerForeignDocSection'),
        });
    }

    // ── Traveller manager (add/remove additional travellers) ──
    const passengersContainer = document.getElementById('passengersContainer')!;
    const addPassengerBtn     = document.getElementById('addPassengerBtn')!;
    setupTravellerManager({
        container:           passengersContainer,
        addButton:           addPassengerBtn,
        searchUrl:           urls.searchTravellers,
        getAgencyId:         () => getAgencyId(form),
        getAgencyName:       () => getAgencyName(),
        getOfferCache:       offerCache.getAll,
        getSelectedOfferIds: () => getAllSelectedOfferIds(accommodationSelect, transportSelect, insuranceSelect),
        onTravellerAdded: (card) => {
            setupTravellerRow(card);
            syncNewTravellerCard(card, recalcPrices);
            recalcPrices();
        },
        onTravellerRemoved: () => recalcPrices(),
    });

    // ── AI features ──
    initAiPropose(form);
    initContractAssistant(form);

    prefill.applyOffer();
});
